import { normalizeLedgerFields, transactionTitleSnapshot } from "../../lib/ledger-field-compat";
import { transactionMatches } from "../../lib/transactions";
import type { ExpenseInput, Transaction } from '../../types';
import type { PriceObservation, TransactionReceiptEdit } from '../../types/receipt';
import { receiptReviewChange } from './receipt-review-change';
import { normalizedMeasure } from '../../types/receipt';

export type EditJournal = {
  clearing?: boolean;
  transaction: Transaction;
  originalPrices: PriceObservation[];
  expense: ExpenseInput;
  started: boolean;
  transactionSaved: boolean;
  completed: string[];
  receiptEdit?: TransactionReceiptEdit;
};
type Request = <T>(path: string, body?: unknown) => Promise<T>;
// Names are a read projection of a linked Payee, not a change to the transaction.
// Older edit journals do not have this projection yet.
const snapshot = (tx: Transaction) => transactionTitleSnapshot({ ...tx, payeeName: undefined });
const itemValue = (p: { name: string; brand: string; group: string; total: string | number; measure: string | number | null; unit: string; pack: string; notes?: string }) => {
  const measure = normalizedMeasure(String(p.measure ?? ''), p.unit);
  return JSON.stringify([p.name.trim(), p.brand.trim(), (p.group || p.name).trim(), Number(p.total), measure?.measure ?? null, measure?.unit || '', p.pack.trim(), (p.notes || '').trim()]);
};
export async function saveEdit(journal: EditJournal, request: Request, persist: (value: EditJournal) => void) {
  // Persist before dispatch: a refresh must retain the same new-item IDs.
  const j = normalizeLedgerFields(structuredClone(journal));
  j.started = true;
  persist(j);
  {
    // Only this entry is re-checked, so only this entry is read — in the same shape the ledger shows.
    const { transaction: current } = await request<{ transaction: Transaction | null }>(`transactions/view?id=${encodeURIComponent(j.transaction.id)}`);
    if (!current) throw new Error('This transaction was deleted. No further changes were made.');
    if (!transactionMatches(current, j.expense) || (j.receiptEdit && !j.transactionSaved)) {
      if (j.transactionSaved) throw new Error('This transaction changed after your partial save. Reopen to review it.');
      if (!transactionMatches(current, j.expense) && snapshot(current) !== snapshot(j.transaction)) throw new Error('This transaction changed elsewhere. Reopen it and review the latest values.');
      await request('transactions/update', { id: current.id, expected: current, expense: j.expense,
        ...(j.receiptEdit ? { receiptReview: receiptReviewChange(j.receiptEdit) } : {}) });
    }
    j.transactionSaved = true;
    persist(j);
  }
  const { observations } = await request<{ observations: PriceObservation[] }>('prices');
  const desired = j.expense.trackedItems || [];
  const actions = [
    ...desired.map(item => ({ id: item.id, item, original: j.originalPrices.find(p => p.observationId === item.id) })),
    ...j.originalPrices.filter(p => !desired.some(i => i.id === p.observationId)).map(original => ({ id: original.observationId, item: undefined, original })),
  ];
  for (const action of actions) {
    if (j.completed.includes(action.id)) continue;
    const { item, original } = action;
    const observationId = original?.observationId || `${j.transaction.id}:${action.id}`;
    // Sheets uses the predictable transaction:item observation id. D1 mints an OBS id, but
    // returns the stable create request key on reads so a reload after a lost response can still
    // recognize the row without sending the write again.
    const current = observations.find(p =>
      p.observationId === observationId || (!original && p.requestId === action.id),
    );
    if (original && current && current.transactionId !== j.transaction.id) throw new Error(`${original.name} was moved elsewhere. Reopen to review it.`);
    if (original && !current && item) throw new Error(`${original.name} was deleted elsewhere. Reopen to review it.`);
    if (item && current && itemValue(current) === itemValue(item)) {
      // Includes a successful write whose response was lost.
    } else if (original) {
      if (current && itemValue(current) !== itemValue(original)) throw new Error(`${original.name} changed elsewhere. Your older edit has not overwritten it.`);
      if (current) {
        if (item) await request('prices/update', { ...current, ...item, observationId, revision: current.revision });
        else await request('prices/delete', { observationId, revision: current.revision });
      }
    } else if (item) {
      if (current) throw new Error(`${item.name} already exists with different values. Reopen to review it.`);
      await request('prices/add', { transactionId: j.transaction.id, requestId: item.id, ...item });
    }
    j.completed.push(action.id);
    persist(j);
  }
  return j;
}
